import React, { useMemo } from 'react';
import { components } from 'react-select';

// components
import LoadingSpinner from '../../../../../../components/nav/LoadingSpinner';
import SearchableSelect, {
    SearchableSelectType,
} from 'components/inputs/searchableSelect';
import AampeTooltip from 'components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import ICONS from '../../../../../../constants/icons';

// hooks
import useDatasetsQuery from 'hooks/datasets/useDatasetsQuery';

// models
import { DatasetListModel } from 'models/dataset.model';

import { disabledDatasetTooltipDescription } from '../contants';

interface DatasetSelectDropdownProps {
    isEditable: boolean;
    selectedDatasetId?: number;
    setSelectedDatasetId: (id: number) => void;
    appliedDatasetFieldIds?: number[];
}

const DatasetSelectDropdown: React.FC<
    React.PropsWithChildren<DatasetSelectDropdownProps>
> = ({
    isEditable,
    selectedDatasetId,
    setSelectedDatasetId,
    appliedDatasetFieldIds,
}) => {
    const { data, isPending: isLoading } = useDatasetsQuery();

    const datasetOptions = useMemo<SearchableSelectType[]>(() => {
        if (!data) return [];
        const activeDatasets = (data ?? []).filter(
            (dataset: DatasetListModel) => !dataset.archived_at,
        );
        return activeDatasets.map((dataset: DatasetListModel) => ({
            label: `${dataset.name} (${dataset.record_count})`,
            value: dataset.id,
        }));
    }, [data]);

    const currentValue = useMemo<SearchableSelectType | undefined>(() => {
        if (!selectedDatasetId) return undefined;
        const match = datasetOptions?.find(
            (dataset) => dataset.value === selectedDatasetId,
        );
        if (!match) return undefined;

        return match;
    }, [datasetOptions, selectedDatasetId]);

    const isDisabled = useMemo<boolean>(() => {
        return !!appliedDatasetFieldIds?.length;
    }, [appliedDatasetFieldIds]);

    const CustomSingleValue = (props: any) => {
        return (
            <components.SingleValue {...props}>
                <LabelPrefix>
                    <span>Dataset</span>
                    <LabelValue isDisabled={isDisabled}>
                        {props.data.label}
                    </LabelValue>
                    {isDisabled && (
                        <AampeTooltip title={disabledDatasetTooltipDescription}>
                            <InfoWrapper>
                                <Info />
                            </InfoWrapper>
                        </AampeTooltip>
                    )}
                </LabelPrefix>
            </components.SingleValue>
        );
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <StyledSearchableSelect
            options={datasetOptions}
            value={currentValue}
            name="datasetSelect"
            onChange={(_, selected) => {
                if (!Array.isArray(selected)) {
                    setSelectedDatasetId(selected.value as number);
                }
            }}
            isDisabled={isDisabled || !isEditable}
            components={{
                SingleValue: CustomSingleValue,
            }}
            containerStyles={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'end',
                width: '240px',
            }}
        />
    );
};
export default DatasetSelectDropdown;

const StyledSearchableSelect = styled(SearchableSelect)({
    width: '100%',
    zIndex: 101,
});

const LabelPrefix = styled.span({
    color: 'inherit',
    display: 'flex',
    justifyContent: 'space-evenly',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(2, 0),
});

const LabelValue = styled.span<{ isDisabled?: boolean }>(({ isDisabled }) => ({
    color: isDisabled
        ? aampeTheme.colors.neutral09
        : aampeTheme.colors.solidA01,
    fontWeight: aampeTheme.weights.heavy,
    overflow: 'hidden',
}));

const InfoWrapper = styled.div({
    display: 'flex',
    pointerEvents: 'auto',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center',
});

const Info = styled(ICONS.info)(() => ({
    cursor: 'pointer',
    color: aampeTheme.colors.gray600,
    height: '18px',
    width: '18px',
}));
